import axiosInstance from '../axios.config';
import { apiError } from '../apiError';
import { ApiResponse } from 'types';

/**
 * Flows imported from a switch.
 *
 * Separate from `ivrApps.service` on purpose: that one reads the registry
 * (`ivr_apps`) — one row per operator/service, with a name and a state count and
 * nothing else. This reads the flow itself, the node graph the importer built.
 *
 * The editor needs this because the two are filed independently: every registry
 * row sits under the root organisation while an imported flow belongs to the
 * tenant that owns it. Opening an app for editing therefore cannot know which
 * organisation holds its flow, which is why `/for-app` takes no organizationId
 * and resolves across the caller's accessible organisations instead.
 */

/** A node as the importer emitted it — already the editor's shape, not a state row. */
export interface IvrFlowNode {
  id?: string;
  type?: string;
  label?: string;
  [key: string]: any;
}

export interface IvrFlow {
  id?: number;
  organizationId?: string;
  flowName?: string;
  description?: string;
  status?: string;
  version?: number;
  startNodeId?: string;
  nodes?: IvrFlowNode[];
  variables?: Record<string, any>;
  settings?: Record<string, any>;
  enabled?: boolean;
}

export const ivrFlowsService = {
  /**
   * The imported flow behind one app, or null when there is none.
   *
   * A 404 is the ordinary answer for an app nobody has imported, so it is
   * reported as `null` rather than as an error — the caller starts from a blank
   * flow in that case, which is what the editor did before this existed.
   */
  async forApp(operatorId: string, serviceId: string): Promise<ApiResponse<IvrFlow | null>> {
    try {
      const res = await axiosInstance.get<ApiResponse<IvrFlow>>('/ivr/flows/for-app', {
        params: { operatorId, serviceId },
      });
      return { success: true, data: res.data?.data ?? null } as ApiResponse<IvrFlow | null>;
    } catch (e: any) {
      if (e?.response?.status === 404) {
        return { success: true, data: null } as ApiResponse<IvrFlow | null>;
      }
      return apiError(e, 'Failed to load the imported flow') as any;
    }
  },

  /** Every flow belonging to one organisation. */
  async listForOrganization(organizationId: string): Promise<ApiResponse<IvrFlow[]>> {
    try {
      const res = await axiosInstance.get<ApiResponse<IvrFlow[]>>('/ivr/flows', {
        params: { organizationId },
      });
      return { success: true, data: res.data?.data || [] } as ApiResponse<IvrFlow[]>;
    } catch (e: any) {
      return apiError(e, 'Failed to load IVR flows') as any;
    }
  },
};
